/**
 * A combatant's **frozen weapon profiles** — the damage, reach, and tag facts snapshotted
 * off the compendium at spawn — plus weapon instance identity and the Loading resource key.
 *
 * The freeze is the invariant: a spawn adapter resolves each weapon out of the compendium
 * **once**, and the resolver reads damage dice, type, and mitigation tags off *this* list
 * and never reaches back into the compendium. That is what keeps the reducer and resolver
 * self-contained on tactical state, and what makes an encounter replay byte-stable.
 *
 * Every tag flag here is **additive and optional**: an absent flag means the weapon doesn't
 * carry that tag (the common case, and every hand-built test fixture), so read them
 * defensively (`weapon.loading`, `weapon.selfHeat ?? 0`) and never assume presence.
 */

/**
 * A Lancer damage type. Normalized **lowercase** — the resolver lowercases the compendium's
 * capitalized `"Kinetic"` at spawn. `kinetic`/`energy`/`explosive` are the three ordinary
 * types; `burn`/`heat` are the special harm types (both ignore armor — rulebook "ARMOR AND
 * RESISTANCE" / "HEAT"). `variable` is the weapon-data placeholder (the wielder chooses a
 * type at fire time); an unresolved `variable` is treated as `kinetic` for mitigation and
 * flagged.
 */
export type DamageType = "kinetic" | "energy" | "explosive" | "burn" | "heat" | "variable";

/**
 * A normalized weapon **range** entry — one `{type, val}` from the compendium with its type
 * lowercased to a known {@link RangeKind} and `val` coerced to a number. A weapon carries an
 * array (a Mortar is `[{kind:"range",val:15},{kind:"blast",val:1}]`). `range`/`threat` are
 * the targeting reach (how far you can hit); `blast`/`burst`/`line`/`cone` are the
 * area-template kinds the VTT paints around or along the target.
 */
export type RangeKind = "range" | "threat" | "blast" | "line" | "cone" | "burst";
export type WeaponRange = { kind: RangeKind; val: number };

/**
 * A weapon's **frozen profile** — its parsed damage dice plus the mitigation and legality
 * tags, flattened to booleans/values here so the resolver reads them without re-parsing tag
 * refs. Snapshotted onto the {@link CombatantBase} at spawn; the named-weapon lookup in a
 * `RequestAttack` resolves against this list.
 */
export type WeaponProfile = {
  /**
   * The weapon's compendium id — data-lookup identity only (name/damage/tags/display),
   * NOT combat identity. Two mounts carrying the same weapon type freeze two
   * {@link WeaponProfile} entries that share this `id`; {@link instanceId} is what
   * tells them apart.
   */
  id: string;
  /**
   * **Instance identity** — a unique id per *equipped copy* of a weapon, distinct from the
   * compendium {@link id} two mounts of the same weapon type share. Derived at freeze time
   * from the weapon's position in its source (mount/slot index for a mech loadout weapon;
   * frame-integrated weapons, NPC weapon features, and a mid-fight `edit_combatant_base`
   * append each use their own namespaced scheme so ids never collide across schemes). This
   * is what "has this weapon fired" tracking keys on; `id` stays the compendium id
   * throughout.
   *
   * **Additive/optional**: every *freeze* site sets it (spawn, mid-fight equip), so any
   * profile living on a real `Combatant.base.weapons[]` has one. It is optional only so
   * hand-built fixtures aren't forced to invent one — those don't exercise instance-identity
   * logic.
   */
  instanceId?: string;
  /** Display name (for the play-by-play). */
  name: string;
  /** Parsed damage dice: `count`d`sides` + `bonus`, of `type`. */
  damage: WeaponDamage;
  /**
   * **Rider damage** — extra damage entries beyond the first, frozen in source order (Aegis
   * Light Laser's Burn rider on an Energy primary; Pyro Napalm Bomb's Heat rider on a Burn
   * primary). {@link damage} remains the sole to-hit-linked entry — it owns the crit-dice
   * pool, Overkill, Reliable, and the area shared pool; riders are extra typed damage
   * instances that ride along on a hit. A single-entry weapon (the common case) omits this
   * field entirely rather than freezing `riders: []`.
   */
  riders?: WeaponDamage[];
  /**
   * The weapon's frozen **range / area** entries (Range, Threat, Blast, Line, Cone, Burst),
   * snapshotted off the compendium at spawn. Drives the VTT's attack-range overlay (the red
   * reach wash plus the hovered AoE template) AND the attack gate: `checkAttackRange` is a
   * hard wall at both the standard-attack and Overwatch-reaction referee gates — an
   * out-of-reach shot is rejected before any dice.
   *
   * A weapon with **no usable range data** (this field absent/empty, and no `tg_thrown`
   * reach either) is the one carve-out: range reads as *unknown*, not zero, so the gate
   * stands down and the attack resolves unbounded. A multi-profile weapon freezes a `range`
   * per profile, since an alternate firing mode can reshape its reach or area.
   */
  range?: WeaponRange[];
  /** `tg_ap` — armor-piercing: ignores the target's armor. */
  ap: boolean;
  /** `tg_overkill` — reroll 1s, 1 heat to the wielder per reroll. */
  overkill: boolean;
  /**
   * `tg_heat_self` value (N) — **Heat (Self)**: this weapon deals N heat to its OWN wielder
   * every time it is FIRED (RAW Core pp.104–106), unconditional on hit or miss and distinct
   * from Overkill's reroll-driven heat. Read as `weapon.selfHeat ?? 0`.
   */
  selfHeat?: number;
  /** `tg_reliable` value (N) — minimum damage dealt **even on a miss**; `0`/absent = none. */
  reliable: number;
  /**
   * `tg_loading` — **Loading** (RAW Core p.105: "must be reloaded after each use. Mechs can
   * reload with STABILIZE and some systems"). Read as `weapon.loading` (falsy).
   *
   * The FIRED/reloaded state itself is NOT here — it lives on the wielder's
   * `CombatantCurrent.resources[loadedResourceKey(key)]` (ABSENT = loaded; see
   * {@link isWeaponLoaded}), because "is this weapon Loading" is a frozen fact while "is it
   * currently loaded" is combat-mutable state. This flag is only the gate to whether that
   * resource is ever consulted for this weapon.
   */
  loading?: boolean;
  /**
   * `tg_limited {N}` — **Limited** (RAW Core p.105): the weapon's total charge count for the
   * encounter. Read as `weapon.limited` (undefined-checked, never `?? 0` — `0` is not a
   * meaningful cap RAW ever prints).
   *
   * How many charges have been FIRED is NOT here — it lives on the wielder's
   * `CombatantCurrent.affordanceState[key].uses` as a **consumed count** (absent =
   * 0 consumed = full, the OPPOSITE polarity from Loading's binary absent-means-loaded,
   * because Limited counts up rather than toggling). Charges are encounter-durable: scene =
   * encounter, no in-encounter reset. This flag is only the gate to whether that resource is
   * ever consulted.
   *
   * **A separate home from a granted action's frequency cap**: weapon Limited rides the
   * weapon profile, keyed by {@link weaponInstanceKey}, never the granted-action store; the
   * two unify only at the shared read predicate.
   */
  limited?: number;
  /**
   * `tg_smart` — **Smart** (RAW Core p.105: "all attacks with this weapon — including melee
   * and ranged attacks — use the target's E-DEFENSE instead of EVASION. Targets with no
   * E-DEFENSE count as having 8 E-DEFENSE"). Read as `weapon.smart` (falsy).
   *
   * Smart is **not** a tech attack: only the defense stat the to-hit roll checks flips to
   * `edef` — it must not inherit tech-immunity, INVADE routing, or the tech cover exemption.
   * The "no E-DEFENSE counts as 8" fallback applies only inside the Smart branch of that
   * selection (an NPC's frozen `edef` reads with a `0` fallback, so `0` is the "absent"
   * signal there) and is never generalized to the tech path.
   */
  smart?: boolean;
  /**
   * `tg_ordnance` — **Ordnance** (RAW Core p.105: "This weapon can only be fired before the
   * user moves or takes any other actions on their turn, excepting PROTOCOLS. … because of
   * its size, this weapon can't be used against targets in engagement with the user's mech,
   * and cannot be used for OVERWATCH"). Read as `weapon.ordnance` (falsy).
   *
   * The three consequences are gated in `@lancer-vtt/rules`' `economy.ts`: the turn-virgin
   * window, the engaged-target reject (PAIRWISE attacker↔target adjacency + hostility, never
   * the `Engaged` status), and the Overwatch ban. This flag is only the gate to whether
   * those checks are consulted.
   */
  ordnance?: boolean;
  /**
   * `tg_sidearm` — **Sidearm** (RAW Core p.105: "This weapon can be used to FIGHT as a quick
   * action instead of a full action"). A **pilot-only** tag; mech weapons never carry it.
   * `intentCost` reads it off the SELECTED weapon (not just the attacker's kind) to charge
   * Fight 1 point instead of 2.
   *
   * RAW attaches no "twice" clause to the tag itself — the twice-a-turn consequence rides
   * the separate allowance-override seam (`CombatantBase.actionAllowances["fight"] = 2`,
   * stamped at freeze when the loadout carries a Sidearm weapon): a Quick Fight then fits
   * twice in the turn's 2-point budget, while a Full (non-Sidearm) Fight still can't, no
   * matter the allowance. The ordinary budget arithmetic caps it, not a special case.
   */
  sidearm?: boolean;
  /**
   * `tg_recharge` value (N) — **Recharge N+** (RAW Core p.281): once this weapon fires it
   * becomes **discharged** and can't fire again until an NPC's start-of-turn 1d6 recharge
   * roll comes up N or higher. Read as `weapon.recharge ?? 0` (the common case is no
   * Recharge — every mech weapon and most NPC weapons fire every turn). Which instance is
   * currently discharged lives on the wielder's `CombatantCurrent.affordanceState`, keyed by this
   * weapon's {@link weaponInstanceKey}.
   */
  recharge?: number;
  /**
   * The weapon's **innate Accuracy/Difficulty** (RAW Core p.281): a flat bonus (positive =
   * Accuracy, negative = Difficulty) folded into the attack's net accuracy alongside every
   * other accuracy/difficulty source, distinct from {@link attackBonus} (which is a to-hit
   * bonus, not an accuracy-die count). **NPC weapons, and the Engineer's prototype weapon with
   * Tweaked Optics** (`@lancer-vtt/rules` `applyPrototypeConfig`) — a mech weapon's compendium
   * entry carries no such field, so it is otherwise absent for one; the GM's own `--accuracy`
   * CLI flag is a SEPARATE, additive source (this field is never a substitute for it).
   * Frozen at spawn (tier-resolved, like {@link attackBonus}); read as `weapon.accuracy ?? 0`.
   */
  accuracy?: number;
  /**
   * **Usable as either a ranged or a melee weapon, but not both in the same turn** — the Ram
   * Cannon (`npcf_ram_cannon_cataphract`, Core p.297), whose v3 `weapon_type` is
   * `"Heavy Melee/Heavy Cannon"` and whose `range` is `[Range 5, Threat 2]`. Frozen by
   * `freezeNpcWeapon` from a slash-joined `weapon_type` naming Melee and a non-Melee class.
   *
   * With the flag set, {@link isRangedAttack}'s range-shape answer (always "ranged" for this
   * weapon, since Range comes first) is replaced per attack by the resolved mode — melee within
   * Threat, ranged beyond it, or the intent's explicit `mode` (`@lancer-vtt/rules`
   * `attackWeaponMode`). The not-both rule is the per-turn latch
   * {@link import("./actionEconomy").WEAPON_MODE_LATCH_PREFIX}. Absent on every other weapon.
   */
  eitherMode?: true;
  /**
   * **Alternate firing profiles** — a multi-profile weapon (Assault Cannon Standard/Spin-Up;
   * the Apocalypse Rail's Charge-Rail 4/3/2/1; ZF4 SOLIDCORE) carries its *other* selectable
   * profiles here, each a full {@link WeaponProfile} with its own damage/dice/tags and a
   * distinct `id`. The weapon object itself **is its own default profile** (its top-level
   * `damage`/`ap`/… describe the default firing mode), so `profiles` lists only the
   * *alternates*. All are frozen at spawn; resolution selects one via
   * {@link selectWeaponProfile}, by recorded choice or by a state lookup (the Apocalypse Die
   * value). A single-profile weapon omits it and resolves as itself.
   */
  profiles?: WeaponProfile[];
  /**
   * The weapon's frozen **tag refs** — `[{ id: "tg_ap" }, { id: "tg_accurate", val: 1 }, …]`,
   * snapshotted off the compendium at spawn. The effects engine's overlay loader gathers a
   * weapon's tag-borne effects (the `{VAL}` templates — Accurate {VAL}, Knockback {VAL}) off
   * this list and binds each template with the ref's `val` at gather time.
   *
   * The A2 mitigation flags (`ap`/`overkill`/`reliable`) are flattened separately and still
   * drive the damage path; this list is the *authored source* the overlay binds, read only
   * when the effects engine is on. A weapon with no overlay-modeled tags has none here.
   */
  tags?: { id: string; val?: number }[];
  /**
   * **Per-weapon to-hit bonus.** NPC weapons carry their own `attack_bonus` (`[1,2,3]` per
   * tier) — the NPC stat block has *no* class-level attack bonus (its axes are
   * hull/agility/systems/engineering), so the bonus is authoritative per weapon. Frozen at
   * spawn (tier-resolved); the resolver prefers it:
   * `weapon.attackBonus ?? attacker.base.attackBonus ?? 0`. A mech weapon carries no
   * per-weapon bonus and falls back to the attacker's Grit.
   */
  attackBonus?: number;
  /**
   * **Display-only** rule text — the NPC weapon feature's `effect` prose (e.g. Missile
   * Swarm's "deals double damage to Lock On"). Shown in the inspector; never read by the
   * resolver.
   */
  effect?: string;
  /**
   * The weapon's **mount size** (`"Auxiliary" | "Main" | "Heavy" | "Superheavy"`),
   * snapshotted off the compendium weapon's `mount` field at spawn. Frozen so combat-time
   * legality (the Barrage gate — a Superheavy fires alone) reads the SAME size string the
   * build-time loadout invariant does, without re-deriving from the roster mech. A frozen
   * base without it, or an NPC weapon (which has no mount), is treated as a normal
   * non-Superheavy weapon by the Barrage gate.
   */
  mount?: string;
  /**
   * lancer-data's **`skirmish: true`** — this profile may be fired in a Skirmish (and so on
   * Overwatch) even though its weapon is Superheavy. Only the Leviathan Heavy Assault Cannon's
   * Standard profile sets it (Core p.134); its Spin-Up profile doesn't ("can no longer use the
   * Leviathan with SKIRMISH"). Frozen on the alternate profile that declares it, and on the base
   * weapon when its own entry or its first (default) profile does. Read via
   * `superheavyBarsSkirmish`. Absent everywhere else.
   */
  skirmish?: true;
  /**
   * lancer-data's **`no_attack: true`**: the weapon can't be attacked with by any action. Only a
   * reaction's forced attack fires it (the Autopod's Autonomous Assault). `checkAttackWeapon` and
   * the Barrage gates refuse it. Absent everywhere else.
   */
  noAttack?: true;
  /**
   * How many **attacks** one use of this weapon makes: an NPC weapon's per-tier `attacks` field
   * ("At T2, this weapon can make two attacks at a time, targeting either the same character or
   * two different ones. At T3, it can make three", the Drum Shotgun). Each is its own attack roll
   * and damage. Frozen at spawn, tier-resolved, and only when above 1; absent means one attack.
   * The Skirmish and Barrage attack lock reads it through `weaponAttackCount`, letting the weapon
   * fire this many times in the one action.
   */
  attacks?: number;
  /**
   * The weapon's **class** (`"CQB" | "Cannon" | "Launcher" | "Melee" | "Nexus" | "Rifle" |
   * "Special" | "Spool Weapon" | "???"`), snapshotted off the compendium weapon's `type`
   * field at spawn — the `weapon_type` predicate op's one source. Frozen the same way
   * `mount` is, at **one** site (`freezeWeaponProfile`), so every PC-weapon namespace that
   * routes through it carries this field.
   *
   * **Mech weapons only.** A pilot-gear weapon's compendium entry reuses the SAME `type`
   * field name for a different vocabulary (`"Weapon"`/`"Armor"`/`"Gear"`, the gear-kind
   * classifier, not a weapon class), so the freeze validates against the closed weapon-class
   * set and drops anything outside it — a pilot-gear weapon's `type` is honestly omitted,
   * never `"Weapon"`. An NPC weapon feature carries no weapon-class field either, RAW giving
   * NPC weapons no Rifle/CQB-style taxonomy; that is by construction, not an oversight.
   */
  type?: string;
  /**
   * An NPC weapon's **class word** off its `weapon_type` line (`"Main Rifle"` → `"Rifle"`; the Ram
   * Cannon's `"Heavy Melee/Heavy Cannon"` → `"Cannon"`, its non-Melee half, since its melee mode is
   * decided per attack). Read only by the attack fx rule (`attackFx` in `@lancer-vtt/rules`), which
   * reads `type ?? fxClass`; it is a separate field so no `weapon_type` predicate starts matching
   * NPC weapons. Absent on mech and pilot weapons, and on an NPC line with no class word.
   */
  fxClass?: string;
}

/**
 * A frozen, **display-only** NPC feature — the non-weapon half of an NPC class's
 * `base_features` (its Systems / Traits / Reactions / Tech), snapshotted at spawn into
 * {@link CombatantBase.npcFeatures} so the inspector renders a real stat block. Weapon
 * features do NOT live here (they freeze into resolvable {@link CombatantBase.weapons});
 * this list is purely what the UI prints, never read by the resolver. `effect` is the
 * feature's primary rule text (its `effect` prose, or the first action's detail when the
 * rule lives in an action); `tags` are its frozen tag refs.
 */
export type FrozenNpcFeature = {
  /**
   * The source compendium feature id (`npcf_climber_sniper`) — what a surface classifies the
   * feature by (`classifyMechanization`, the "not automated — GM rules this" marker). Absent
   * only for a data feature with no id.
   */
  id?: string;
  /** Display name. */
  name: string;
  /** Feature category — `"System" | "Trait" | "Reaction" | "Tech" | …` (verbatim from the data). */
  type: string;
  /** The feature's primary rule text (display only). */
  effect?: string;
  /** Frozen tag refs (`[{ id: "tg_recharge", val: 5 }, …]`) for display. */
  tags?: { id: string; val?: number }[];
  /**
   * The SAME `system:${j}` key as this feature's {@link CombatantBase.equippedSystems} twin
   * — present only for a `type === "System"` feature that also froze into `equippedSystems`
   * — so the inspector can render a DESTROYED tag on this display row via
   * {@link isItemDestroyed} without re-deriving the correlation. Absent for every
   * Weapon/Trait/Reaction/Tech feature.
   */
  instanceId?: string;
}

/**
 * Select the active {@link WeaponProfile} of a (possibly multi-profile) weapon. A
 * `profileId` names one of the weapon's alternate `profiles`; absent or unmatched falls back
 * to the weapon itself (its default firing mode). Pure — the selection seam the resolver and
 * the atom interpreter both call. A single-profile weapon always returns itself.
 */
export function selectWeaponProfile(
  weapon: WeaponProfile,
  profileId: string | undefined,
): WeaponProfile {
  if (profileId && profileId !== weapon.id) {
    const alt = weapon.profiles?.find((p) => p.id === profileId);
    if (alt) return alt;
  }
  return weapon;
}

/**
 * The **combat-identity key** of a frozen weapon — the id every "which physical weapon is
 * this / has it fired" site keys on (`selectWeapon`, the Barrage fired-weapon ledger, the
 * frontend choosers). It is the per-instance {@link WeaponProfile.instanceId} when present
 * (the real answer — two mounts of the same weapon type freeze distinct instance ids),
 * falling back to the compendium {@link WeaponProfile.id} only for hand-built fixtures that
 * never set one, which don't exercise duplicate-instance logic.
 *
 * **This is NOT a data lookup** — name, damage, tags, effects overlay, and firing-profile
 * selection all keep reading `id` directly; this key answers *identity* only. An alt firing
 * profile shares its parent's `instanceId`, so switching firing modes never changes a
 * weapon's identity key: the `profileId` disambiguates the mode, this key the physical copy.
 */
export function weaponInstanceKey(weapon: WeaponProfile): string {
  return weapon.instanceId ?? weapon.id;
}

/**
 * A human-readable placement label for a frozen weapon's instance key — decodes the
 * namespaced `instanceId` schemes the freeze sites mint (`${mountIndex}:${slotIndex}`,
 * 0-based, for a loadout weapon; `integrated:${index}` for a frame-integrated one;
 * `manual:${index}` for a mid-fight GM-appended one; `npc:${index}` for an NPC's flat-frozen
 * weapon, NPCs having no mount concept) into a 1-based, GM-facing phrase — `"mount 1 slot
 * 1"`, `"integrated weapon"`, `"manually added"`, `"NPC weapon 1"`. Falls back to the raw key
 * for any scheme it doesn't recognize (e.g. a fixture that never set an `instanceId`, so
 * {@link weaponInstanceKey} falls back to the bare compendium id).
 */
export function describeWeaponInstance(weapon: WeaponProfile): string {
  const key = weaponInstanceKey(weapon);
  const mountSlot = /^(\d+):(\d+)$/.exec(key);
  if (mountSlot) return `mount ${Number(mountSlot[1]) + 1} slot ${Number(mountSlot[2]) + 1}`;
  if (key.startsWith("integrated:")) return "integrated weapon";
  if (key.startsWith("manual:")) return "manually added";
  const npcSlot = /^npc:(\d+)$/.exec(key);
  if (npcSlot) return `NPC weapon ${Number(npcSlot[1]) + 1}`;
  return key;
}

/**
 * One candidate line for a weapon-ambiguity reject's candidate list — `"<instance key>
 * (<name>, <placement>)"`, e.g. `"0:0 (Vulture DMR, mount 1 slot 1)"`. Shared by the
 * server-side wall (an omitted `--weapon` on a 2+-weapon loadout) and the CLI-side
 * ambiguous-ref reject so both name the same weapons the same way — the candidate list
 * doubles as key discovery either way a GM hits it.
 */
export function formatWeaponCandidate(weapon: WeaponProfile): string {
  return `${weaponInstanceKey(weapon)} (${weapon.name}, ${describeWeaponInstance(weapon)})`;
}

/**
 * The `current.resources` key a weapon's **Loading** state rides — namespaced by the
 * weapon's {@link weaponInstanceKey} so two mounts of the same weapon type track
 * independently, exactly like Recharge's `discharged` map. It lives in `@lancer-vtt/domain`
 * (next to `weaponInstanceKey`, not in `@lancer-vtt/rules`' `economy.ts`) so both the fire
 * gate and the fire-emit (`resolve/weaponConsume.ts`) can import it without the resolver reaching
 * back into `economy.ts`, whose `economy/` modules import `selectWeapon` from the `resolve.ts`
 * facade — a cycle. Every reader and writer
 * of Loading state derives the key through this one function.
 */
/**
 * The combatant id of a **thrown weapon lying downrange** (`tg_thrown`, Core p.105: "a thrown
 * weapon comes to rest in an adjacent space to its target and must be retrieved as a free action
 * while adjacent to that weapon before it can be used again"). The throw spawns an object with this
 * id beside the target; while it stands, the weapon can't attack (`checkLoaded`), and its owner's
 * Retrieve action (`tg_thrown#Retrieve`) despawns it.
 */
export function thrownWeaponObjectId(attackerId: string, weaponKey: string): string {
  return `${attackerId}:thrown:${weaponKey}`;
}

/**
 * The `current.resources` key a weapon's **Loading** state rides — see the doc above this
 * function's caller list. (Kept beside {@link thrownWeaponObjectId}, the other weapon-readiness id.)
 */
export function loadedResourceKey(weaponKey: string): string {
  return `loaded:${weaponKey}`;
}

/**
 * Read a weapon-instance's raw Loading state off a combatant's `current.resources` —
 * **ABSENT = loaded** (the sparse-delta convention: an absent key means pristine). A Loading
 * weapon that has never fired, and one that was never Loading to begin with, simply has no
 * `loaded:<key>` entry, and both read as loaded via this `?? 1` default; the key is never
 * seeded at spawn or freeze. The one shared read every consult site must use, so they can
 * never drift into checking the key by hand.
 */
export function isWeaponLoaded(resources: Record<string, number> | undefined, weaponKey: string): boolean {
  return (resources?.[loadedResourceKey(weaponKey)] ?? 1) >= 1;
}

/** A parsed weapon damage roll: `count`d`sides` + flat `bonus`, of `type`. */
export type WeaponDamage = {
  count: number;
  sides: number;
  /** Flat bonus added to the dice total (the `+3` in `1d3+3`); `0` if none. */
  bonus: number;
  type: DamageType;
}
